import { Node } from 'reactflow';

// This function builds a parent map from an array of nodes.
// The parent map is a record where each key is a parent node ID and the value is an array of child node IDs.
// This is useful for organizing nodes in a tree-like structure.
// For example, if node A has children B and C, the map will look like { A: [B, C] }.
export function buildParentMap(nodes: Node[]): Record<string, string[]> {
    return nodes.reduce(
        (acc, node) => {
            if (node.parentNode) {
                if (!acc[node.parentNode]) {
                    acc[node.parentNode] = [];
                }
                acc[node.parentNode].push(node.id);
            }
            return acc;
        },
        {} as Record<string, string[]>,
    );
}

// This function collects all descendant node IDs from a parent map.
// It takes a parent map and a parent ID, and returns an array of all descendant node IDs.
// For example, if node A has children B and C, and C has a child D, the result will be [B, C, D].
export function collectDescendantNodeIdsFromMap(parentMap: Record<string, string[]>, parentId: string): string[] {
    const childIds = parentMap[parentId] || [];

    return childIds.flatMap((childId) => [childId, ...collectDescendantNodeIdsFromMap(parentMap, childId)]);
}

// This function creates a lookup map from an array of items based on a key generated by the provided function.
// e.g.:
// const nodesById = useMemo(() => {
//   return createLookupMap(canvasNodes, (node) => node.id);
// }, [canvasNodes]);
export function createLookupMap<T extends { [key: string]: any }, K extends keyof any>(
    items: T[],
    getKey: (item: T) => K | undefined | null,
): Record<K, T> {
    const map = {} as Record<K, T>;
    for (const item of items) {
        const key = getKey(item);
        if (key !== undefined && key !== null) {
            map[key] = item;
        }
    }
    return map;
}

// Defer expensive work until the browser is idle, reducing jank during interactions like panning, zooming, or dragging nodes
export const requestIdle: (cb: () => void) => void =
    typeof window !== 'undefined' && 'requestIdleCallback' in window
        ? window.requestIdleCallback
        : (cb) => setTimeout(cb, 0);
